import "core-js/modules/es6.promise";
import "core-js/modules/es6.object.to-string";
import "core-js/modules/es6.array.find";
import "core-js/modules/es6.regexp.replace";

/**
 * Contentful Management API Client. Contains methods which allow access to
 * any operations that can be performed with a management token.
 * @packageDocumentation
 */

/**
 * Types for meta information found across the different entities in Contentful
 * @namespace Meta
 */

/**
 * System metadata. See <a href="https://www.contentful.com/developers/docs/references/content-delivery-api/#/introduction/common-resource-attributes">Common Resource Attributes</a> for more details.
 * @memberof Meta
 * @typedef Sys
 * @prop {string} type
 * @prop {string} id
 * @prop {Meta.Link} space
 * @prop {string} createdAt
 * @prop {string} updatedAt
 * @prop {number} revision
 */

/**
 * Link to another entity. See <a href="https://www.contentful.com/developers/docs/concepts/links/">Links</a> for more details.
 * @memberof Meta
 * @typedef Link
 * @prop {string} type - type of this entity. Always link.
 * @prop {string} id
 * @prop {string} linkType - type of this link. If defined, either Entry or Asset
 */
import { createRequestConfig } from 'contentful-sdk-core';
import errorHandler from './error-handler';
import entities from './entities';
/**
 * Creates API object with methods to access functionality from Contentful's
 * Management API
 * @param {Object} params - API initialization params
 * @prop {Object} http - HTTP client instance
 * @prop {Function} shouldLinksResolve - Link resolver preconfigured with global setting
 */

export default function createClientApi(_ref) {
  var http = _ref.http;
  var _entities$space = entities.space,
      wrapSpace = _entities$space.wrapSpace,
      wrapSpaceCollection = _entities$space.wrapSpaceCollection;
  var wrapUser = entities.user.wrapUser;
  var _entities$personalAcc = entities.personalAccessToken,
      wrapPersonalAccessToken = _entities$personalAcc.wrapPersonalAccessToken,
      wrapPersonalAccessTokenCollection = _entities$personalAcc.wrapPersonalAccessTokenCollection;
  var _entities$organizatio = entities.organization,
      wrapOrganization = _entities$organizatio.wrapOrganization,
      wrapOrganizationCollection = _entities$organizatio.wrapOrganizationCollection;
  var wrapUsageCollection = entities.usage.wrapUsageCollection;

  function getOrganizations() {
    var baseURL = http.defaults.baseURL.replace('/spaces/', '/organizations/');
    return http.get('', {
      baseURL: baseURL
    }).then(function (response) {
      return wrapOrganizationCollection(http, response.data);
    }, errorHandler);
  }

  function getOrganization(id) {
    var baseURL = http.defaults.baseURL.replace('/spaces/', '/organizations/');
    return http.get('', {
      baseURL: baseURL
    }).then(function (response) {
      var org = response.data.items.find(function (org) {
        return org.sys.id === id;
      });

      if (!org) {
        var error = new Error("No organization was found with the ID ".concat(id, " instead got ").concat(JSON.stringify(response)));
        error.status = 404;
        error.statusText = 'Not Found';
        return Promise.reject(error);
      }

      return wrapOrganization(http, org);
    }, errorHandler);
  }

  function getSpaces() {
    var query = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    return http.get('', createRequestConfig({
      query: query
    })).then(function (response) {
      return wrapSpaceCollection(http, response.data);
    }, errorHandler);
  }

  function getSpace(id) {
    return http.get(id).then(function (response) {
      return wrapSpace(http, response.data);
    }, errorHandler);
  }

  function createSpace(data, organizationId) {
    return http.post('', data, {
      headers: organizationId ? {
        'X-Contentful-Organization': organizationId
      } : {}
    }).then(function (response) {
      return wrapSpace(http, response.data);
    }, errorHandler);
  }

  function getOrganizationUsage(organizationId) {
    var query = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var baseURL = http.defaults.baseURL.replace('/spaces/', "/organizations/".concat(organizationId, "/organization_periodic_usages"));
    return http.get('', {
      baseURL: baseURL,
      params: query
    }).then(function (response) {
      return wrapUsageCollection(http, response.data);
    }, errorHandler);
  }

  function getSpaceUsage(organizationId) {
    var query = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var baseURL = http.defaults.baseURL.replace('/spaces/', "/organizations/".concat(organizationId, "/space_periodic_usages"));
    return http.get('', {
      baseURL: baseURL,
      params: query
    }).then(function (response) {
      return wrapUsageCollection(http, response.data);
    }, errorHandler);
  }

  function getCurrentUser() {
    var baseURL = http.defaults.baseURL.replace('/spaces/', '/users/me/');
    return http.get('', {
      baseURL: baseURL
    }).then(function (response) {
      return wrapUser(http, response.data);
    }, errorHandler);
  }

  function createPersonalAccessToken(data) {
    var baseURL = http.defaults.baseURL.replace('/spaces/', '/users/me/access_tokens');
    return http.post('', data, {
      baseURL: baseURL
    }).then(function (response) {
      return wrapPersonalAccessToken(http, response.data);
    }, errorHandler);
  }

  function getPersonalAccessToken(tokenId) {
    var baseURL = http.defaults.baseURL.replace('/spaces/', '/users/me/access_tokens');
    return http.get(tokenId, {
      baseURL: baseURL
    }).then(function (response) {
      return wrapPersonalAccessToken(http, response.data);
    }, errorHandler);
  }

  function getPersonalAccessTokens() {
    var baseURL = http.defaults.baseURL.replace('/spaces/', '/users/me/access_tokens');
    return http.get('', {
      baseURL: baseURL
    }).then(function (response) {
      return wrapPersonalAccessTokenCollection(http, response.data);
    }, errorHandler);
  }

  function rawRequest(opts) {
    return http(opts).then(function (response) {
      return response.data;
    }, errorHandler);
  }

  return {
    getSpaces: getSpaces,
    getSpace: getSpace,
    createSpace: createSpace,
    getOrganization: getOrganization,
    getOrganizations: getOrganizations,
    getCurrentUser: getCurrentUser,
    createPersonalAccessToken: createPersonalAccessToken,
    getPersonalAccessToken: getPersonalAccessToken,
    getPersonalAccessTokens: getPersonalAccessTokens,
    rawRequest: rawRequest,
    getOrganizationUsage: getOrganizationUsage,
    getSpaceUsage: getSpaceUsage
  };
}